import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Quel est le délai pour un remplacement de vitre cassée à Lyon ?",
    a: "Nous intervenons sous 24 à 48 heures après votre appel, 7j/7, dans tout Lyon et la Métropole de Lyon. En cas d'urgence (vitre brisée présentant un risque de sécurité), nous faisons le maximum pour intervenir le jour même.",
  },
  {
    q: "Proposez-vous le remplacement de double vitrage ?",
    a: "Oui, nous remplaçons tous types de vitrages : simple, double, triple, feuilleté de sécurité, ou vitrage isolant renforcé (VIR). Nous prenons les mesures sur place et commandons un vitrage aux dimensions exactes de votre menuiserie.",
  },
  {
    q: "Mon double vitrage est embué de l'intérieur, que faire ?",
    a: "Un double vitrage embué entre les deux verres signifie que le joint d'étanchéité est défectueux. Le vitrage a perdu ses propriétés isolantes et doit être remplacé. Nous intervenons à Lyon pour le remplacement de vos vitrages embués.",
  },
];

const servicesVitrier = [
  {
    icon: 'ri-contrast-drop-2-line',
    title: 'Remplacement vitre cassée',
    description: 'Vitre brisée, fêlée ou impactée ? Nous sécurisons et remplaçons votre vitrage en urgence à Lyon.',
    href: '/remplacement-vitre-cassee-lyon/',
  },
  {
    icon: 'ri-stack-line',
    title: 'Remplacement double vitrage',
    description: 'Double vitrage à changer : vitrage isolant, mesures précises, pose soignée. Économies d\'énergie garanties.',
    href: '/remplacement-double-vitrage-lyon/',
  },
  {
    icon: 'ri-drop-line',
    title: 'Double vitrage embué',
    description: 'Condensation entre les verres ? Votre vitrage est HS. Remplacement rapide du vitrage isolant à Lyon.',
    href: '/double-vitrage-embue-lyon/',
  },
  {
    icon: 'ri-layout-masonry-line',
    title: 'Survitrage',
    description: 'Isolation sans changer la fenêtre : pose d\'un survitrage sur menuiserie existante. Solution économique.',
    href: '/survitrage-lyon/',
  },
  {
    icon: 'ri-alarm-warning-line',
    title: 'Vitrier urgence',
    description: 'Vitre cassée en pleine nuit ? Effraction ? Sécurisation et remplacement express, 7j/7 à Lyon.',
    href: '/vitrier-urgence-lyon/',
  },
  {
    icon: 'ri-artboard-line',
    title: 'Miroiterie',
    description: 'Miroirs sur mesure, verre décoratif, découpe de précision. Pose soignée à Lyon et sa métropole.',
    href: '/miroiterie-lyon/',
  },
];

export default function VitrierPilier() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Vitrier à Lyon : remplacement et réparation de vitrage',
    provider: {
      '@type': 'HomeAndConstructionBusiness',
      name: 'DFPRO Lyon',
      telephone: '+33980803684',
    },
    areaServed: [{ '@type': 'City', name: 'Lyon' }, { '@type': 'AdministrativeArea', name: 'Métropole de Lyon' }],
    description: 'Remplacement de vitrage cassé, double vitrage, survitrage et miroiterie à Lyon. Intervention 24-48h, 7j/7, devis gratuit, garantie 10 ans.',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Vitrier à Lyon', item: 'https://depanfenetres-lyon.fr/vitrier-lyon/' },
    ],
  };

  return (
    <>
      <SEOHead
        title="Vitrier à Lyon | Remplacement de vitrage 7j/7 | DFPRO"
        description="Vitre cassée, double vitrage à remplacer, vitrage embué ? Vitrier à Lyon, intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans. Appelez DFPRO Lyon."
        canonicalUrl="https://depanfenetres-lyon.fr/vitrier-lyon/"
        ogTitle="Vitrier à Lyon — Remplacement de vitrage rapide 7j/7 | DFPRO"
        ogDescription="Vitre cassée, double vitrage, vitrage embué ? Vitrier Lyon 24-48h, devis gratuit, garantie 10 ans."
        jsonLd={{ ...serviceJsonLd, ...faqJsonLd, ...breadcrumbJsonLd }}
      />

      <Header />

      <main>
        {/* Breadcrumb */}
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-secondary-400">
              <a href="/" className="hover:text-primary-500 transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">Vitrier à Lyon</span>
            </nav>
          </div>
        </div>

        {/* Hero */}
        <section className="relative w-full min-h-[420px] md:min-h-[480px] flex items-center bg-background-50">
          <div className="absolute inset-0">
            <img
              src="/images/vitrier-lyon-hero-2026.webp"
              alt="Vitrier à Lyon — remplacement de vitrage par DFPRO Lyon, technicien en intervention sur une fenêtre"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/35 to-foreground-900/50"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-tight">
                Vitrier à Lyon : remplacement et réparation de vitrage
              </h1>
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                Vitre cassée, double vitrage à remplacer, vitrage embué ? Nos vitriers interviennent chez vous à Lyon et dans la métropole pour tout remplacement de vitrage. Diagnostic et devis gratuits.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler le 09 80 80 36 84
                </a>
                <a href="/contact/" className="inline-flex items-center gap-2 border-2 border-background-50/60 hover:border-background-50 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-file-list-3-line w-5 h-5 flex items-center justify-center"></i>
                  Devis gratuit
                </a>
              </div>
            </div>
          </div>
        </section>

        <ReassuranceBar />

        {/* Introduction */}
        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-800 mb-5">
                  Votre vitrier à Lyon pour tous vos besoins en vitrage
                </h2>
                <div className="space-y-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  <p>
                    Une vitre cassée peut rapidement devenir un problème de sécurité et d'isolation. Que vous soyez confronté à une vitre brisée, un double vitrage embué ou un vitrage à changer pour améliorer votre isolation thermique, DFPRO Lyon met à votre disposition des vitriers qualifiés qui interviennent rapidement dans toute la Métropole de Lyon.
                  </p>
                  <p>
                    Nous prenons en charge tous les types de vitrages : <strong className="text-foreground-800">vitre simple, double vitrage, vitrage feuilleté de sécurité, vitrage isolant renforcé (VIR)</strong>. Nous intervenons sur fenêtres, portes-fenêtres, baies vitrées, vérandas et tout type de menuiserie. Nos techniciens viennent avec le matériel nécessaire pour prendre les mesures précises et poser un vitrage neuf adapté à votre bâti existant.
                  </p>
                  <p>
                    Le remplacement d'une vitre ne s'improvise pas : dimensions au millimètre, type de verre adapté à l'usage (isolant, sécurité, acoustique), pose sans défaut d'étanchéité. C'est un travail de spécialiste que nos équipes maîtrisent parfaitement. Nous intervenons à Lyon et dans les communes alentour : Villeurbanne, Caluire-et-Cuire, Vénissieux, Bron, Vaulx-en-Velin, Saint-Priest, Tassin-la-Demi-Lune, Écully, Sainte-Foy-lès-Lyon et bien d'autres.
                  </p>
                  <p>
                    Chaque intervention de vitrerie bénéficie de notre <strong className="text-foreground-800">garantie 10 ans</strong>, qui couvre à la fois le vitrage posé et la main-d'œuvre. Un gage de sérénité pour votre habitat.
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 h-fit">
                <h3 className="font-heading font-semibold text-base text-foreground-800 mb-4">Nos services de vitrerie</h3>
                <nav className="space-y-2">
                  {[
                    { label: 'Remplacement vitre cassée', href: '/remplacement-vitre-cassee-lyon/' },
                    { label: 'Remplacement double vitrage', href: '/remplacement-double-vitrage-lyon/' },
                    { label: 'Double vitrage embué', href: '/double-vitrage-embue-lyon/' },
                    { label: 'Survitrage', href: '/survitrage-lyon/' },
                    { label: 'Vitrier urgence', href: '/vitrier-urgence-lyon/' },
                    { label: 'Miroiterie', href: '/miroiterie-lyon/' },
                  ].map((link, idx) => (
                    <a key={idx} href={link.href} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-500 transition-colors py-1.5 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <a href="tel:+33980803684" className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer">
                    <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                    Appeler — 09 80 80 36 84
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Grille services */}
        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">
                Tous nos services de vitrerie à Lyon
              </h2>
              <p className="mt-3 text-sm md:text-base text-secondary-500 max-w-2xl mx-auto">
                Du remplacement de vitre simple à la miroiterie sur mesure, nous couvrons l'ensemble des besoins en vitrage.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {servicesVitrier.map((srv, idx) => (
                <a key={idx} href={srv.href} className="group block bg-background-50 rounded-lg border border-background-200 hover:border-primary-200 transition-all duration-200 cursor-pointer p-5 md:p-6">
                  <div className="w-12 h-12 rounded-md bg-primary-50 flex items-center justify-center mb-4">
                    <i className={`${srv.icon} text-primary-400 text-xl w-5 h-5 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-base text-foreground-800 mb-2">{srv.title}</h3>
                  <p className="text-sm text-secondary-500 leading-relaxed">{srv.description}</p>
                  <span className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-primary-500 group-hover:text-primary-600 transition-colors">
                    En savoir plus
                    <i className="ri-arrow-right-line text-xs w-3 h-3 flex items-center justify-center"></i>
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Types de vitrages */}
        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">
                Types de vitrages que nous posons
              </h2>
              <p className="mt-3 text-sm md:text-base text-secondary-500 max-w-2xl mx-auto">
                Nous travaillons avec tous les types de verre pour répondre à chaque besoin : sécurité, isolation, acoustique.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {[
                { title: 'Vitrage simple', desc: 'Verre float standard, pour les cloisons intérieures ou les remplacements de vitrage décoratif.' },
                { title: 'Double vitrage', desc: 'Vitrage isolant 4/16/4 standard ou VIR à isolation renforcée. La solution pour vos fenêtres et baies.' },
                { title: 'Vitrage feuilleté', desc: 'Verre de sécurité composé de deux feuilles avec film PVB. Idéal pour les portes et baies exposées.' },
                { title: 'Vitrage acoustique', desc: 'Verre spécial anti-bruit pour les logements exposés au trafic routier. Confort sonore amélioré.' },
              ].map((item, idx) => (
                <div key={idx} className="bg-background-100 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-checkbox-blank-circle-fill text-primary-400 text-sm w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <h3 className="font-heading font-semibold text-base text-foreground-800">{item.title}</h3>
                  <p className="mt-2 text-sm text-secondary-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">
                Questions fréquentes — Vitrier à Lyon
              </h2>
            </div>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Besoin d'un vitrier à Lyon ?"
          subtitle="Appelez-nous au 09 80 80 36 84. Devis gratuit, intervention sous 24-48h, 7j/7."
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>

      <Footer />
    </>
  );
}